import { PopIn } from '@words-happen/ui-components';
import type React from 'react';
import type { CSSProperties } from 'react';
import { useLayoutEffect, useRef, useState } from 'react';
import { BoardTileFace } from '../board/board-tile-face';
import type { TileData } from '../board/board-types';
import styles from './tile-wordmark.module.scss';

export interface TileWordmarkProps {
  /** One string per row of tiles, e.g. ['WORDS', 'HAPPEN']. */
  lines: string[];
  /** Tile size in px. The upper bound: tiles shrink to fit a narrower container. */
  size?: number;
  /** Horizontal nudge per row, in px at full size, e.g. [0, 10] for a hand-placed look. */
  offsets?: number[];
  /** Pop the tiles in, one after another. */
  animate?: boolean;
  /** The points badge on every tile. */
  points?: number;
  /** Accessible name. Defaults to the lines in title case, e.g. "Words Happen". */
  label?: string;
  className?: string;
}

// Spacing as a share of tile size: 5px between tiles and 7px between rows at 48px.
const TILE_GAP_RATIO = 5 / 48;
const ROW_GAP_RATIO = 7 / 48;
const MIN_SIZE = 16;

const TILE_STAGGER = 0.07;
const ROW_STAGGER = 0.4;

const titleCase = (line: string) => line.charAt(0).toUpperCase() + line.slice(1).toLowerCase();

/**
 * The largest tile size, up to `maxSize`, at which every row fits `availableWidth`. A row is its tiles,
 * the gaps between them, and room for its offset on both sides, since the row stays centred.
 */
export const fitTileSize = (lines: string[], offsets: number[], maxSize: number, availableWidth: number): number => {
  if (!(availableWidth > 0)) return maxSize;

  const widestFactor = Math.max(
    ...lines.map((line, row) => {
      const count = [...line].length;
      const offsetRatio = Math.abs(offsets[row] ?? 0) / maxSize;
      return count + Math.max(count - 1, 0) * TILE_GAP_RATIO + 2 * offsetRatio;
    }),
  );

  return Math.max(MIN_SIZE, Math.min(maxSize, Math.floor(availableWidth / widestFactor)));
};

const wordmarkTile = (letter: string, row: number, col: number, points: number): TileData => ({
  id: `wordmark-${row}-${col}`,
  row,
  col,
  letter,
  dir: 'h',
  points,
});

/**
 * A word spelled in the board's own tiles (BoardTileFace), one row per line. Tiles shrink to fit the
 * container's width, capped at `size`. Assistive tech hears one name ("Words Happen"), not each letter.
 */
export const TileWordmark: React.FC<TileWordmarkProps> = ({
  lines,
  size = 48,
  offsets = [],
  animate = false,
  points = 1,
  label,
  className,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [tileSize, setTileSize] = useState(size);

  // Keyed on content, so inline arrays don't resubscribe on every render.
  const linesKey = lines.join('\n');
  const offsetsKey = offsets.join(',');

  // Measured before paint, then kept up to date as the container resizes.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const rows = linesKey.split('\n');
    const nudges = offsetsKey ? offsetsKey.split(',').map(Number) : [];
    const measure = () => setTileSize(fitTileSize(rows, nudges, size, el.clientWidth));
    measure();

    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [linesKey, offsetsKey, size]);

  const scale = tileSize / size;

  return (
    <div
      ref={ref}
      role="img"
      aria-label={label ?? lines.map(titleCase).join(' ')}
      className={[styles.wordmark, className].filter(Boolean).join(' ')}
      style={
        {
          '--wordmark-tile-gap': `${tileSize * TILE_GAP_RATIO}px`,
          '--wordmark-row-gap': `${tileSize * ROW_GAP_RATIO}px`,
        } as CSSProperties
      }
    >
      {lines.map((line, row) => (
        <div
          key={row}
          aria-hidden="true"
          className={styles.row}
          style={{ '--wordmark-offset': `${(offsets[row] ?? 0) * scale}px` } as CSSProperties}
        >
          {[...line].map((letter, i) => (
            <PopIn key={i} play={animate} delay={row * ROW_STAGGER + i * TILE_STAGGER}>
              <BoardTileFace tile={wordmarkTile(letter, row, i, points)} cellSize={tileSize} gap={0} />
            </PopIn>
          ))}
        </div>
      ))}
    </div>
  );
};
